import { useQuery } from '@apollo/client';
import {
  ComparatorContext,
  orderStatusQueryApollo,
  useField,
  useWizardService,
} from '@kizi/comparator-core';
import React, { useContext, useEffect } from 'react';
import { PageLayout, PageLayoutProps } from '../../PageLayout';
import { LoadingPage, LoadingPageProps } from './LoadingPage';
import { useRedirectOnBack } from '@kizi/comparator-core';

export interface CheckoutPageGraphProps extends PageLayoutProps {
  loadingPage: LoadingPageProps;
  goBackStepPosition?: number;
}

export const CheckoutPageGraph: React.FC<CheckoutPageGraphProps> = ({
  loadingPage,
  goBackStepPosition,
  ...pageLayoutProps
}) => {
  const wizardService = useWizardService();
  const { api, name } = useContext(ComparatorContext);

  useEffect(() => {
    if (localStorage.getItem('orderCompleted') === 'true') {
      localStorage.removeItem('orderCompleted');
      wizardService?.send({ type: 'GOTO', index: 1, meta: { reload: true } });
    }
  }, []);

  useRedirectOnBack(goBackStepPosition);

  const orderIdField = useField('apply.orderId');

  if (orderIdField?.value === 'error') {
    return <>Something went wrong...</>;
  }

  const { loading, error, data, stopPolling } = useQuery(
    orderStatusQueryApollo,
    {
      variables: { orderId: orderIdField?.value },
      pollInterval: 2000,
      ...([
        'home',
        'os-home',
        'travel',
        'os-travel',
        'os-solar-panels',
        'moneysure-event-based',
      ].includes(name)
        ? {
            context: {
              uri: `${process.env.KIZI_PUBLIC_DOMAIN}/order/graphql`,
              headers: {
                'Authorization':
                  'Bearer ' +
                  (api.credentials.token ||
                    localStorage.getItem('accessToken')),
              },
            },
          }
        : {}),
    },
  );

  useEffect(() => {
    wizardService?.send([
      {
        type: 'SET_FORM_FIELDS',
        name: 'checkout',
        fields: [
          { name: 'orderStatus', value: data?.orderStatus?.status },
          {
            name: 'underwritingResult',
            value: data?.orderStatus?.underwritingResultStatus,
          },
        ],
      },
    ]);
  }, [data, wizardService]);

  if (
    loading ||
    data?.orderStatus?.status === 'QUEUED' ||
    data?.orderStatus?.status === 'PROCESSING'
  )
    return <LoadingPage {...loadingPage} />;

  if (error) {
    stopPolling();
    return <>Something went wrong...</>;
  }

  if (data?.orderStatus?.status === 'ERROR') {
    stopPolling();
  } else if (
    data?.orderStatus?.status === 'DONE' ||
    data?.orderStatus?.status === 'UNDER_REVIEW'
  ) {
    stopPolling();
    window?.dataLayer?.push({
      event: 'purchase',
      ecommerce: {
        transactionId: '',
        affiliation: '',
        value: 1,
        currency: 'EUR',
        coupon: '',
        items: [],
      },
    });
    localStorage.removeItem(`kizi-comparator-${name}`);
    localStorage.removeItem('apollo-cache-persist');
    localStorage.removeItem('lastProductId');
  }

  return (
    <PageLayout
      contentStyles={{
        mx: 'auto',
        span: [12, 12, 12, 12],
        width: [null, null, null, 810],
      }}
      {...pageLayoutProps}
    />
  );
};
